import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Upload, FileText, Image, Paperclip, X, Loader2, AlertCircle, ChevronDown, Plus } from 'lucide-react';
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { cn } from "../../components/ui/utils";
import { serviceDeskApi } from "../../utils/service-desk-api";
import api from "../../utils/api";
import { toast } from "sonner";
import { DynamicFormFields } from "../../components/service-desk/DynamicFormFields";
import { useAuth } from "../../context/AuthContext";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import { FormField } from "../../components/service-desk/DynamicFormFields";

/**
 * Determine whether a dynamic form field value is "empty" for a required field.
 * Covers every field type rendered by DynamicFormFields, including file uploads
 * (an attachment field is only complete when at least one file is attached) and
 * idarah_data selectors.
 */
const isFieldValueEmpty = (field: FormField, value: unknown): boolean => {
    if (value === undefined || value === null) return true;

    if (field.type === 'file' || field.type === 'checkbox' || field.type === 'multi_select') {
        return !Array.isArray(value) || value.length === 0;
    }

    if (field.type === 'idarah_data') {
        return (Array.isArray(value) && value.length === 0) || (typeof value === 'string' && value.trim() === '');
    }

    return typeof value === 'string' && value.trim() === '';
};

const findMissingRequiredField = (fields: FormField[], values: Record<string, unknown>): FormField | null => {
    for (const field of fields) {
        if (['heading', 'static_text'].includes(field.type)) continue;
        if (!field.required) continue;
        if (isFieldValueEmpty(field, values[field.id])) return field;
    }
    return null;
};

export default function CreateRequest() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const ability = React.useContext(AbilityContext);
  const { deskId, formId } = useParams<{ deskId: string, formId?: string }>();
  const isDedicated = !!(deskId && deskId !== 'new');
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialFormId = formId || queryParams.get('form');
  const isPreview = location.pathname.includes('/preview') || queryParams.get('preview') === 'true';

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingDesks, setIsLoadingDesks] = useState(true);
  const [isFetchingFields, setIsFetchingFields] = useState(false);
  const [serviceDesks, setServiceDesks] = useState<any[]>([]);
  const [formName, setFormName] = useState<string>('');
  const [formFields, setFormFields] = useState<any[]>([]);

  const [formData, setFormData] = useState({
    serviceDeskId: '',
    selectedFormId: '',
    formVersionId: '',
    title: '',
    priority: 'Medium'
  });
  const [dynamicFields, setDynamicFields] = useState<Record<string, any>>({});

  const canSubmitAnywhere = ability.can(ACTIONS.MANAGE, RESOURCES.SERVICE_DESK_REQUESTER) || 
                             ability.can(ACTIONS.MANAGE, RESOURCES.SERVICE_DESK_ADMIN) || 
                             ability.can(ACTIONS.MANAGE, RESOURCES.SERVICE_DESK_TEAM_ADMIN);

  const handleDeskChange = (deskId: string) => {
    setFormData(prev => ({ 
      ...prev, 
      serviceDeskId: deskId, 
      selectedFormId: '', 
      formVersionId: '' 
    }));
    setFormName('');
    setFormFields([]);
    setDynamicFields({});
  };

  const handleFormChange = async (formId: string) => {
    if (!formId) return;
    setIsFetchingFields(true);
    try {
        const response = await serviceDeskApi.getFormDetails(formId);
        if (response.success) {
            const form = response.form;
            const sortedVersions = [...(form.versions || [])].sort((a: any, b: any) => b.versionNumber - a.versionNumber);
            const activeVersion = sortedVersions.find((v: any) => isPreview || v.status === 'PUBLISHED') || sortedVersions[0];
            
            if (activeVersion) {
                setFormName(form.name);
                setFormFields(activeVersion.fields || []);
                setFormData(prev => ({ 
                  ...prev, 
                  selectedFormId: formId, 
                  formVersionId: activeVersion.id.toString() 
                }));
                setDynamicFields({});
            }
        }
    } catch (error) {
        console.error("Error fetching form details:", error);
        toast.error("Failed to load form fields.");
    } finally {
        setIsFetchingFields(false);
    }
  };

  const fetchDesks = async () => {
    setIsLoadingDesks(true);
    try {
      const response = await serviceDeskApi.getServiceDesks({ retired: false });
      if (response.success) {
        // Filter based on authorization
        const authorizedDesks = response.desks.filter((d: any) => {
            if (!canSubmitAnywhere) return false;
            
            const isDeskOpen = d.submissionControl === 'open';
            const isUserAuthorized = d.authorizedRequesters?.some((u: any) => String(u.id) === String(currentUser?.id));
            
            return isDeskOpen || isUserAuthorized;
        });

        setServiceDesks(authorizedDesks);

        const effectiveDeskId = (deskId && deskId !== 'new') ? deskId : '';
        if (effectiveDeskId) {
          const desk = response.desks.find((d: any) => String(d.id) === String(effectiveDeskId));
          if (desk) {
            setFormData(prev => ({ ...prev, serviceDeskId: effectiveDeskId }));
            if (initialFormId) {
                handleFormChange(initialFormId);
            }
          }
        }
      }
    } catch (error) {
      console.error("Error fetching desks:", error);
    } finally {
      setIsLoadingDesks(false);
    }
  };

  useEffect(() => {
    fetchDesks();
  }, [deskId, initialFormId]);

  const selectedDesk = serviceDesks.find(d => String(d.id) === String(formData.serviceDeskId));
  const availableForms = selectedDesk?.forms?.filter((f: any) => {
      const isNotRetired = !f.isRetired;
      const hasPublishedVersion = (f.versions || []).some((v: any) => v.status === 'PUBLISHED');
      return isNotRetired && (isPreview || hasPublishedVersion);
  }) || [];
  const hasNoForms = !!(formData.serviceDeskId && !isLoadingDesks && availableForms.length === 0);
  const hasNoDesks = !isLoadingDesks && serviceDesks.length === 0;

  const handleDynamicFieldChange = (fieldId: string, value: any) => {
    setDynamicFields(prev => ({ ...prev, [fieldId]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isPreview || hasNoForms || hasNoDesks || isFetchingFields) return;
    
    if (!formData.serviceDeskId || !formData.title) {
      toast.error("Please fill in all required fields.");
      return;
    }

    if (!formData.formVersionId) {
        toast.error("Please select a request type.");
        return;
    }

    // Block submission until every field marked mandatory (including file
    // attachments) is completed. Native `required` cannot cover custom field
    // types (idarah_data selectors, radio/checkbox groups) or file inputs that
    // are hidden, so enforce explicitly against the active version's fields.
    const missingField = findMissingRequiredField(formFields, dynamicFields);
    if (missingField) {
        toast.error(`Please complete the required field: "${missingField.label}".`);
        return;
    }

    setIsSubmitting(true);
    try {
      // 1. Prepare data and identify files to upload
      const finalDynamicFields = { ...dynamicFields };
      const filesToUpload: Record<string, File[]> = {};

      for (const [fieldId, value] of Object.entries(dynamicFields)) {
          if (Array.isArray(value) && value.length > 0 && value[0] instanceof File) {
              filesToUpload[fieldId] = value as File[];
              // Placeholder in data while we upload
              finalDynamicFields[fieldId] = [];
          }
      }

      // 2. Upload files first
      const uploadedFilesMetadata: Record<string, {name: string, url: string, size: number, key: string}[]> = {};
      
      for (const [fieldId, files] of Object.entries(filesToUpload)) {
          uploadedFilesMetadata[fieldId] = [];
          for (const file of files) {
              const uploadFormData = new FormData();
              uploadFormData.append('image', file);
              uploadFormData.append('context', 'service-desk');
              try {
                  const uploadResult = await api.post('/api/upload/s3', uploadFormData, { skipErrorToast: true });
                  if (uploadResult.url) {
                      uploadedFilesMetadata[fieldId].push({
                          name: file.name,
                          url: uploadResult.url,
                          size: uploadResult.size || file.size,
                          key: uploadResult.key
                      });
                  }
              } catch (err) {
                  console.error(`Error uploading ${file.name} for field ${fieldId}:`, err);
              }
          }
          // Update data with metadata instead of File objects
          finalDynamicFields[fieldId] = uploadedFilesMetadata[fieldId];
      }

      // 3. Create the request with metadata in data
      const response = await serviceDeskApi.createRequest({
          title: formData.title,
          priority: formData.priority.toUpperCase(),
          formVersionId: formData.formVersionId,
          data: { 
            ...finalDynamicFields 
          }
      });
      
      if (response.success) {
        const requestId = response.request.id;

        // 4. Register them as official attachments so they show up in the sidebar/list
        for (const [fieldId, metadataList] of Object.entries(uploadedFilesMetadata)) {
            for (const metadata of metadataList) {
                try {
                    await serviceDeskApi.addAttachment(requestId, {
                        filename: metadata.name,
                        url: metadata.url,
                        size: metadata.size,
                        key: metadata.key
                    });
                } catch (err) {
                    console.error("Error linking attachment:", err);
                }
            }
        }

        toast.success("Request submitted successfully!");
        
        // Handle dynamic redirection back to origin.
        // Fallback is the new request's detail view, not the desk page: requesters
        // are usually not desk members, and the desk management page is forbidden
        // to them (backend returns 403), which made a successful submit look failed.
        const returnPath = location.state?.from || `/service-desk/view/${requestId}`;
        navigate(returnPath);
      } else {
        toast.error(response.message || "Failed to submit request.");
      }
    } catch (error) {
      console.error("Submission error:", error);
      toast.error("An error occurred during submission.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen -m-6 bg-white pb-12">
      {/* Header Section - Full Width */}
      <div className="w-full border-b border-gray-200 p-6 sm:p-8 shrink-0 bg-white">
        <div className="w-full space-y-4">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center text-sm text-gray-500 hover:text-gray-900 transition-colors font-medium"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            {isPreview ? "Exit Preview" : "Back"}
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{isPreview ? (formName || "Form Preview") : "Create New Request"}</h1>
            <p className="text-gray-500 mt-1 font-medium">
              {isPreview ? `Testing how the ${formName.toLowerCase() || 'request form'} will look to users` : "Submit a request to the appropriate service desk"}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content Area - Centered */}
      <div className="flex-1 overflow-y-auto px-6 py-8">
        <div className="max-w-2xl mx-auto w-full space-y-8 pb-10">
          {isPreview && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3 text-amber-800 animate-in fade-in slide-in-from-top-2 duration-300">
                  <AlertCircle className="w-5 h-5" />
                  <p className="text-sm font-medium">You are in <strong>Preview Mode</strong>. The form is fully interactive but submission is disabled.</p>
              </div>
          )}

          <form id="create-request-form" onSubmit={handleSubmit} className="space-y-8">
            {/* Service Desk Dropdown */}
            <div className="space-y-2">
              <label className="text-sm font-bold text-gray-700 uppercase tracking-wider">Service Desk *</label>
              <div className="relative">
                <select 
                  className="w-full h-11 px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/20 appearance-none disabled:opacity-50 transition-all"
                  value={formData.serviceDeskId}
                  onChange={(e) => handleDeskChange(e.target.value)}
                  required
                  disabled={isLoadingDesks || isDedicated || isPreview || hasNoDesks}
                >
                  <option value="" disabled>{isLoadingDesks ? "Loading desks..." : "Select a service desk"}</option>
                  {serviceDesks.map(desk => (
                    <option key={desk.id} value={desk.id}>{desk.name}</option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none">
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                </div>
              </div>
              {hasNoDesks && (
                <div className="flex items-center gap-2 mt-1.5 text-amber-600 animate-in fade-in slide-in-from-top-1 duration-200">
                  <AlertCircle className="w-4 h-4" />
                  <p className="text-sm font-medium italic">No service desks are currently available for you to submit requests.</p>
                </div>
              )}
            </div>

            {hasNoForms && (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
                    <div className="flex items-start gap-3 text-gray-800">
                        <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
                        <div>
                            <p className="font-bold">No Request Types Available</p>
                            <p className="text-sm font-medium opacity-90 mt-1">
                                This service desk does not have any published forms yet. You cannot submit a request until at least one form is created and published.
                            </p>
                        </div>
                    </div>
                    <div className="flex justify-end">
                        <Button 
                            className="w-full sm:w-auto gap-2 bg-gray-600 hover:bg-gray-700 text-white font-bold uppercase tracking-tight text-xs h-10 rounded-lg shadow-md"
                            onClick={() => navigate(`/service-desk/${formData.serviceDeskId}/forms/new`)}
                        >
                            <Plus className="w-4 h-4" />
                            Create new Request Form
                        </Button>
                    </div>
                </div>
            )}

            {/* Request Type Dropdown - Conditional */}
            {formData.serviceDeskId && (
              <div className="space-y-2 animate-in fade-in slide-in-from-top-1 duration-200">
                <label className="text-sm font-bold text-gray-700 uppercase tracking-wider">Request Type *</label>
                <div className="relative">
                  <select 
                    className="w-full h-11 px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/20 appearance-none disabled:opacity-50 transition-all"
                    value={formData.selectedFormId}
                    onChange={(e) => handleFormChange(e.target.value)}
                    required
                    disabled={isPreview || hasNoForms || hasNoDesks || isFetchingFields || (isDedicated && !!initialFormId)}
                  >
                    <option value="" disabled>{hasNoForms ? "No forms available" : isFetchingFields ? "Loading fields..." : "Select request type"}</option>
                    {availableForms.map((form: any) => (
                      <option key={form.id} value={form.id}>{form.name}</option>
                    ))}
                  </select>
                  {!hasNoForms && !isFetchingFields && (
                    <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none">
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    </div>
                  )}
                  {isFetchingFields && (
                      <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none">
                          <Loader2 className="h-4 w-4 text-primary animate-spin" />
                      </div>
                  )}
                </div>
              </div>
            )}

            {/* Title */}
            <div className="space-y-2">
              <label className="text-sm font-bold text-gray-700 uppercase tracking-wider">Title *</label>
              <Input 
                className="h-11 bg-gray-50 border-gray-200 rounded-xl focus:ring-primary/20 font-medium" 
                placeholder="Brief summary of your request" 
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                disabled={isPreview || hasNoForms || hasNoDesks}
              />
            </div>

            {/* Priority */}
            <div className="space-y-2">
              <label className="text-sm font-bold text-gray-700 uppercase tracking-wider">Priority *</label>
              <div className={cn("grid grid-cols-4 gap-2", (isPreview || hasNoForms || hasNoDesks) && "opacity-50")}>
                {['Low', 'Medium', 'High', 'Urgent'].map((level) => (
                  <button
                    key={level}
                    type="button"
                    onClick={() => !(isPreview || hasNoForms || hasNoDesks) && setFormData({ ...formData, priority: level })}
                    className={cn(
                      "px-4 py-2 rounded-lg border text-sm font-bold uppercase tracking-tight transition-all duration-200",
                      formData.priority === level 
                        ? "bg-[#0f172a] text-white border-[#0f172a] shadow-md" 
                        : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50 hover:border-gray-300",
                      (isPreview || hasNoForms || hasNoDesks) && "cursor-default"
                    )}
                  >
                    {level}
                  </button>
                ))}
              </div>
            </div>

            {/* Dynamic Form Fields */}
            <DynamicFormFields 
                fields={formFields}
                values={dynamicFields}
                onChange={handleDynamicFieldChange}
                isPreview={isPreview}
                isFetching={isFetchingFields}
                emptyMessage={formData.selectedFormId ? "No additional fields required for this request type." : ""}
            />
          </form>
        </div>
      </div>

      {/* Footer Section */}
      <div className="px-6 py-4 border-t border-border bg-white shrink-0 pb-10 sm:pb-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between gap-3 w-full">
          <Button 
            type="button"
            variant="outline" 
            onClick={() => navigate(-1)}
            className="h-12 px-6 text-sm font-bold border-slate-200 text-slate-700 hover:bg-slate-50 transition-all shrink-0"
          >
            {isPreview ? "Exit" : "Cancel"}
          </Button>
          <Button 
            type="submit"
            form="create-request-form"
            disabled={isSubmitting || isPreview || hasNoForms || hasNoDesks || !formData.selectedFormId || isFetchingFields}
            className="h-12 flex-1 sm:flex-none sm:px-10 gap-2 text-sm font-bold shadow-md bg-slate-900 hover:bg-slate-800 text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? "Submitting..." : "Submit Request"}
          </Button>
        </div>
      </div>
    </div>
  );
}
